import { useMemo } from "react";

const BLUE = "#00c8ff";
const PURPLE = "#6c5ce7";
const CX = 50;
const CY = 50;
const RAD = 31;

/* Fibonacci sphere → unit points */
function fib(count) {
  const pts = [];
  const phi = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const theta = phi * i;
    pts.push([Math.cos(theta) * r, y, Math.sin(theta) * r]);
  }
  return pts;
}

/* Fixed tilt so the sphere reads as a globe, then orthographic project */
function project([x, y, z], ax = -0.34, ay = 0.5) {
  const y1 = y * Math.cos(ax) - z * Math.sin(ax);
  const z1 = y * Math.sin(ax) + z * Math.cos(ax);
  const x2 = x * Math.cos(ay) + z1 * Math.sin(ay);
  const z2 = -x * Math.sin(ay) + z1 * Math.cos(ay);
  return [CX + x2 * RAD, CY - y1 * RAD, z2]; // [px, py, depth(-1..1)]
}

/*
 * Static, GPU-free cosmic backdrop.
 * Visually mirrors the WebGL SpaceScene (node globe + arcs + starfield + glow)
 * but renders as inert SVG/CSS — no Canvas, no requestAnimationFrame, no battery drain.
 * Used for prefers-reduced-motion and low-power / handheld devices.
 */
export default function StaticBackdrop() {
  const nodes = useMemo(() => fib(360).map((p) => project(p)), []);

  const dots = useMemo(
    () =>
      nodes.map(([px, py, d]) => {
        const depth = (d + 1) / 2; // 0 (back) .. 1 (front)
        return {
          cx: px,
          cy: py,
          r: 0.28 + depth * 0.55,
          o: 0.16 + depth * 0.62,
          c: (px * py) % 6 < 1 ? PURPLE : BLUE,
        };
      }),
    [nodes]
  );

  const arcs = useMemo(() => {
    const list = [];
    let seed = 7;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    for (let i = 0; i < 16; i++) {
      const a = nodes[Math.floor(rand() * nodes.length)];
      const b = nodes[Math.floor(rand() * nodes.length)];
      if (!a || !b) continue;
      const dist = Math.hypot(a[0] - b[0], a[1] - b[1]);
      if (dist < 20) continue;
      const mx = (a[0] + b[0]) / 2;
      const my = (a[1] + b[1]) / 2;
      const dx = mx - CX;
      const dy = my - CY;
      const len = Math.hypot(dx, dy) || 1;
      const bulge = 9 + rand() * 6;
      const cx = mx + (dx / len) * bulge;
      const cy = my + (dy / len) * bulge;
      list.push({
        d: `M${a[0].toFixed(2)},${a[1].toFixed(2)} Q${cx.toFixed(2)},${cy.toFixed(2)} ${b[0].toFixed(2)},${b[1].toFixed(2)}`,
        c: i % 2 ? PURPLE : BLUE,
      });
    }
    return list;
  }, [nodes]);

  const stars = useMemo(() => {
    let seed = 42;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    return Array.from({ length: 150 }, () => ({
      cx: rand() * 100,
      cy: rand() * 100,
      r: rand() * 0.16 + 0.04,
      o: rand() * 0.5 + 0.08,
    }));
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="static-backdrop" aria-hidden="true">
      {/* ambient glows */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(620px circle at 50% 44%, rgba(0,200,255,0.13), transparent 60%), radial-gradient(520px circle at 72% 72%, rgba(108,92,231,0.11), transparent 62%)",
        }}
      />
      {/* starfield */}
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
        {stars.map((s, i) => (
          <circle key={i} cx={s.cx} cy={s.cy} r={s.r} fill="#a9dcff" opacity={s.o} />
        ))}
      </svg>
      {/* node globe */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{ width: "min(92vw, 640px)", aspectRatio: "1 / 1" }}
      >
        <svg viewBox="0 0 100 100" className="h-full w-full" style={{ filter: "drop-shadow(0 0 26px rgba(0,200,255,0.22))" }}>
          <defs>
            <radialGradient id="sb-core" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#03202b" stopOpacity="0.5" />
              <stop offset="72%" stopColor="#03202b" stopOpacity="0.14" />
              <stop offset="100%" stopColor="#03202b" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx={CX} cy={CY} r={RAD} fill="url(#sb-core)" />
          {/* wire shell */}
          <g stroke={BLUE} strokeWidth="0.14" fill="none" opacity="0.13">
            <circle cx={CX} cy={CY} r={RAD} />
            <ellipse cx={CX} cy={CY} rx={RAD} ry={RAD * 0.4} />
            <ellipse cx={CX} cy={CY} rx={RAD * 0.4} ry={RAD} />
            <ellipse cx={CX} cy={CY} rx={RAD} ry={RAD * 0.72} />
          </g>
          {/* connection arcs */}
          <g fill="none" strokeWidth="0.22" opacity="0.5">
            {arcs.map((a, i) => (
              <path key={i} d={a.d} stroke={a.c} />
            ))}
          </g>
          {/* nodes */}
          <g>
            {dots.map((d, i) => (
              <circle key={i} cx={d.cx} cy={d.cy} r={d.r} fill={d.c} opacity={d.o} />
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
}
